import * as React from 'react';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Container from '@mui/material/Container';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import { useTheme } from '@mui/material/styles';

const pages = [
  { label: 'Home', id: 'hero' },
  { label: 'Why Us', id: 'why' },
  { label: 'Our Partners', id: 'clients' },
  { label: 'Services', id: 'services' },
  { label: 'Pricing', id: 'pricing' },
  { label: 'Contact', id: 'contact' },
];

function NavBar({ onContactClick }) {
  const theme = useTheme();
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const [scrolled, setScrolled] = React.useState(false);
  const [activeSection, setActiveSection] = React.useState('hero');

  React.useEffect(() => {
    const navOffset = 80;

    const handleScroll = () => {
      setScrolled(window.scrollY > 20);

      let current = pages[0].id;
      for (const page of pages) {
        const el = document.getElementById(page.id);
        if (el && el.getBoundingClientRect().top <= navOffset) {
          current = page.id;
        }
      }
      setActiveSection(current);
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  const openPanel = () => {
    window.open('/login', '_blank');
  };

  return (
    <>
      <AppBar
        position="fixed"
        elevation={0}
        color="transparent"
        sx={{
          background: scrolled ? '#ffffff' : 'transparent',
          backgroundColor: scrolled ? '#ffffff' : 'transparent',
          boxShadow: scrolled ? '0 2px 12px rgba(0, 0, 0, 0.08)' : 'none',
          transition: 'background 0.3s ease, box-shadow 0.3s ease',
        }}
      >
        <Container maxWidth="xl" sx={{ px: { xs: 3, sm: 4, md: 6, lg: 8 } }}>
          <Toolbar disableGutters sx={{ py: 1 }}>
            {/* Logo */}
            <Box sx={{ flexGrow: { xs: 1, md: 0 }, mr: { md: 5 } }}>
              <a href="/" aria-label="StreamWell – Home" style={{ display: 'block' }}>
                <img
                  src="logo.png"
                  alt="StreamWell RTMP Streaming Platform"
                  width="140"
                  height="35"
                  style={{ display: 'block' }}
                />
              </a>
            </Box>

            <Box sx={{ flexGrow: 1 }} />

            {/* Desktop nav links + actions */}
            <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 0.5 }}>
              {pages.map((page) => {
                const isActive = activeSection === page.id;
                return (
                <Button
                  key={page.id}
                  onClick={() => scrollToSection(page.id)}
                  sx={{
                    color: isActive ? theme.palette.primary.main : 'rgba(13, 23, 87, 0.82)',
                    textTransform: 'none',
                    fontSize: '15px',
                    fontWeight: isActive ? 600 : 500,
                    borderRadius: '8px',
                    px: 2,
                    transition: 'color 0.3s ease, background 0.3s ease',
                    '&:hover': {
                      color: '#0d1757',
                      background: 'rgba(5, 15, 102, 0.06)',
                    },
                  }}
                >
                  {page.label}
                </Button>
                );
              })}
              <Button
                onClick={onContactClick}
                variant="contained"
                sx={{
                  background: '#050f66',
                  color: 'white',
                  textTransform: 'none',
                  fontSize: '15px',
                  fontWeight: 600,
                  borderRadius: '8px',
                  px: 3,
                  py: 0.9,
                  ml: 1,
                  boxShadow: 'none',
                  '&:hover': {
                    background: '#0a1882',
                    boxShadow: 'none',
                  },
                }}
              >
                Contact Us
              </Button>
              <Button
                onClick={openPanel}
                variant="outlined"
                sx={{
                  color: '#050f66',
                  borderColor: 'rgba(5, 15, 102, 0.35)',
                  textTransform: 'none',
                  fontSize: '15px',
                  fontWeight: 600,
                  borderRadius: '8px',
                  px: 3,
                  py: 0.9,
                  ml: 0.5,
                  '&:hover': {
                    background: 'rgba(5, 15, 102, 0.04)',
                    borderColor: '#050f66',
                  },
                }}
              >
                Login
              </Button>
            </Box>

            {/* Mobile hamburger */}
            <IconButton
              onClick={() => setDrawerOpen(true)}
              sx={{ display: { md: 'none' }, color: '#0d1757' }}
            >
              <MenuIcon />
            </IconButton>
          </Toolbar>
        </Container>
      </AppBar>

      {/* Mobile Drawer */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{
          sx: {
            width: 280,
            background: 'linear-gradient(160deg, #020b5c 0%, #050f66 100%)',
            color: 'white',
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            px: 2.5,
            py: 2,
          }}
        >
          <a href="/" aria-label="StreamWell – Home" style={{ display: 'block' }}>
            <img src="logo.png" alt="StreamWell RTMP Streaming Platform" width="120" height="34" />
          </a>
          <IconButton color="inherit" onClick={() => setDrawerOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
        <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)' }} />
        <List sx={{ mt: 1 }}>
          {pages.map((page) => {
            const isActive = activeSection === page.id;
            return (
            <ListItem key={page.id} disablePadding>
              <ListItemButton
                onClick={() => { scrollToSection(page.id); setDrawerOpen(false); }}
                sx={{ px: 3, py: 1.5, '&:hover': { background: 'rgba(255,255,255,0.08)' } }}
              >
                <ListItemText
                  primary={page.label}
                  primaryTypographyProps={{
                    fontSize: '16px',
                    fontWeight: isActive ? 600 : 500,
                  }}
                />
              </ListItemButton>
            </ListItem>
            );
          })}
        </List>
        <Box sx={{ p: 3, mt: 'auto', display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Button
            onClick={() => { onContactClick?.(); setDrawerOpen(false); }}
            variant="contained"
            fullWidth
            sx={{
              background: '#050f66',
              color: 'white',
              textTransform: 'none',
              fontSize: '15px',
              fontWeight: 600,
              borderRadius: '10px',
              py: 1.4,
              boxShadow: 'none',
              '&:hover': { background: '#0a1882' },
            }}
          >
            Contact Us
          </Button>
          <Button
            onClick={openPanel}
            variant="outlined"
            fullWidth
            sx={{
              color: 'white',
              borderColor: 'rgba(255,255,255,0.45)',
              textTransform: 'none',
              fontSize: '15px',
              fontWeight: 600,
              borderRadius: '10px',
              py: 1.4,
              '&:hover': { background: 'rgba(255,255,255,0.1)', borderColor: 'white' },
            }}
          >
            Login to Panel
          </Button>
        </Box>
      </Drawer>
    </>
  );
}

export default NavBar;
